import { inject, Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { isVideoOutput } from '../../../cross-team-shared/misc-utils/output-utils';
import { EventTrackingService } from '../../../shared/event-tracking/event-tracking.service';
import { CVFeedbackPopoverService } from '../../feedback/services/cv-feedback-popover.service';
import { getOutputById } from '../../h3ml/dsl/sensor-fusion/sensor-fusion-output.utils';
import { SplitViewService } from '../../h3ml/dsl/sensor-fusion/split-view/split-view.service';
import { TaskWorkspaceStore } from '../../h3ml/state/task-workspace-store';
import { ObjectScopingService } from '../misc-services/object-scoping.service';

/**
 * Manages the feedback list and selection of list items.
 */
@Injectable()
export class FeedbackListService {
  private store = inject(TaskWorkspaceStore);
  private eventTrackingService = inject(EventTrackingService);
  private feedbackPopoverService = inject(CVFeedbackPopoverService);
  private splitViewService = inject(SplitViewService);
  private objectScopingService = inject(ObjectScopingService);

  getSelectedId$() {
    return this.store('feedbackPopover')('selectedId').$;
  }

  openFeedbackList(source: 'reopen' | 'toggle' | 'shape' | 'object') {
    this.openList();
    if (this.store.state().feedbackType === 'shape' && source !== 'reopen') {
      this.eventTrackingService.track({
        id: 'open_feedback_list',
        bucket: 'quality_feedback',
        type: 'shape',
        source,
      });
    }
  }

  closeFeedbackList() {
    this.feedbackPopoverService.closePopover();
    this.clearSelection();
  }

  setSelection(feedbackId: string) {
    this.store('feedbackPopover')('selectedId').set(feedbackId);
  }

  clearSelection() {
    this.store('feedbackPopover')('selectedId').set(undefined);
  }

  openFeedbackListForObject(
    objectIndex: number,
    source: 'shape' | 'object' = 'shape',
  ) {
    this.openFeedbackList(source);

    const objectState = this.store.getObjectStore(objectIndex).state();
    const nestedObjects = [
      objectState,
      ...this.objectScopingService.getNestedObjects(objectIndex),
    ];
    const shapeIndices = nestedObjects.flatMap(
      (object) => object.childShapeIds,
    );

    this.store.batch(() => {
      this.store('feedbackPopover')('shapeFilterIds').set(shapeIndices);
    });
  }

  clearShapeFilter() {
    this.store('feedbackPopover')('shapeFilterIds').set([]);
  }

  isFeedbackListOpen$() {
    return this.store('feedbackPopover')('target').$.pipe(
      map((target) => target === 'list'),
    );
  }

  isFeedbackListOpen() {
    return this.store.state().feedbackPopover.target === 'list';
  }

  switchOutput(outputId: number) {
    const { splitViewOutputName } = this.store.state();
    const output = getOutputById(this.store.state().project, outputId);

    if (splitViewOutputName) {
      if (isVideoOutput(output) && output.name !== splitViewOutputName) {
        this.splitViewService.changeSplitViewOutput(
          output.name,
          'feedbackList',
        );
      }
    }
    this.splitViewService.changeActiveOutput(output.name, 'feedbackList');
  }

  private openList() {
    this.clearShapeFilter();
    this.store('feedbackPopover')('target').set('list');
  }
}
